import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, LogOut, RefreshCw, Trash2 } from "lucide-react";
import { PasswordInput } from "@/components/ui/PasswordInput";

const client = axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
    withCredentials: true,
});

function formatApiErrorDetail(detail) {
    if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const formatDateTime = (iso) => {
    try {
        return new Intl.DateTimeFormat("nl-NL", { dateStyle: "medium", timeStyle: "short" }).format(
            new Date(iso)
        );
    } catch {
        return iso;
    }
};

const ItemCard = ({ kind, item, onToggle, onDelete }) => (
    <div
        data-testid={`${kind}-item-${item.id}`}
        className={`border border-white/10 bg-coal p-6 transition-opacity duration-300 ${
            item.handled ? "opacity-50" : ""
        }`}
    >
        <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="min-w-0">
                <p className="font-display text-xl text-bone">
                    {item.name}
                    {item.company ? <span className="text-ash"> — {item.company}</span> : null}
                </p>
                <a
                    href={`mailto:${item.email}`}
                    className="text-sm text-gold underline decoration-gold/40 underline-offset-4 transition-colors hover:text-bone"
                >
                    {item.email}
                </a>
                {kind === "demo" && (
                    <p className="mt-2 text-sm tracking-wide text-bone">
                        Demo: {item.date} om {item.time}
                    </p>
                )}
                {item.message ? (
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ash">{item.message}</p>
                ) : null}
            </div>
            <div className="flex flex-col items-end gap-3">
                <span
                    data-testid={`${kind}-status-${item.id}`}
                    className={`text-[10px] uppercase tracking-[0.25em] ${
                        item.handled ? "text-ash" : "text-gold"
                    }`}
                >
                    {item.handled ? "Afgehandeld" : "Nieuw"}
                </span>
                <button
                    data-testid={`${kind}-toggle-${item.id}`}
                    onClick={onToggle}
                    className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                >
                    {item.handled ? (
                        <Circle className="h-3.5 w-3.5" />
                    ) : (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                    )}
                    {item.handled ? "Heropen" : "Markeer afgehandeld"}
                </button>
                <button
                    data-testid={`${kind}-delete-${item.id}`}
                    onClick={onDelete}
                    aria-label="Verwijderen"
                    className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-ash transition-colors hover:border-red-400/60 hover:text-red-400"
                >
                    <Trash2 className="h-3.5 w-3.5" /> Verwijder
                </button>
                <span className="text-xs text-ash/60">{formatDateTime(item.timestamp)}</span>
            </div>
        </div>
    </div>
);

export default function Admin() {
    const [auth, setAuth] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState("contact");
    const [messages, setMessages] = useState([]);
    const [demos, setDemos] = useState([]);

    const authed = useCallback(async (fn) => {
        try {
            return await fn();
        } catch (e) {
            if (e.response?.status === 401) {
                await client.post("/auth/refresh");
                return await fn();
            }
            throw e;
        }
    }, []);

    const load = useCallback(async () => {
        try {
            const [c, d] = await authed(() =>
                Promise.all([client.get("/contact"), client.get("/demo-requests")])
            );
            setMessages(c.data);
            setDemos(d.data);
        } catch {
            setAuth(false);
        }
    }, [authed]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await client.get("/auth/me");
                setAuth(data);
            } catch {
                setAuth(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (auth) load();
    }, [auth, load]);

    const login = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await client.post("/auth/login", { email, password });
            setAuth(data);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const logout = async () => {
        try {
            await client.post("/auth/logout");
        } catch {}
        setAuth(false);
    };

    const toggleHandled = async (kind, item) => {
        const next = !item.handled;
        const path = kind === "contact" ? `/contact/${item.id}` : `/demo-requests/${item.id}`;
        const applyTo = (setter) =>
            setter((list) => list.map((x) => (x.id === item.id ? { ...x, handled: next } : x)));
        applyTo(kind === "contact" ? setMessages : setDemos);
        try {
            await authed(() => client.patch(path, { handled: next }));
        } catch {
            load();
        }
    };

    const handleDelete = async (kind, item) => {
        if (!window.confirm("Dit item definitief verwijderen?")) return;
        const path = kind === "contact" ? `/contact/${item.id}` : `/demo-requests/${item.id}`;
        const removeFrom = (setter) => setter((list) => list.filter((x) => x.id !== item.id));
        removeFrom(kind === "contact" ? setMessages : setDemos);
        try {
            await authed(() => client.delete(path));
        } catch {
            load();
        }
    };

    if (auth === null) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-ink">
                <p className="text-xs uppercase tracking-[0.3em] text-ash">Laden…</p>
            </div>
        );
    }

    if (auth === false) {
        return (
            <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-ink px-6">
                <div className="w-full max-w-sm border border-white/10 bg-coal p-8">
                    <p className="text-center font-display text-2xl tracking-[0.12em] text-bone">
                        CLARKE<span className="text-gold">.</span>
                    </p>
                    <p className="mt-2 text-center text-[11px] uppercase tracking-[0.3em] text-ash">
                        Admin — Aanvragen
                    </p>
                    <form onSubmit={login} className="mt-8 space-y-6">
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-[0.3em] text-ash">
                                E-mail
                            </span>
                            <input
                                data-testid="admin-login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="username"
                                className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-base text-bone transition-colors focus:border-gold focus:outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-[0.3em] text-ash">
                                Wachtwoord
                            </span>
                            <PasswordInput
                                testid="admin-login-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-base text-bone transition-colors focus:border-gold focus:outline-none"
                            />
                        </label>
                        {error ? (
                            <p data-testid="admin-login-error" className="text-sm text-red-400">
                                {error}
                            </p>
                        ) : null}
                        <button
                            data-testid="admin-login-submit-button"
                            type="submit"
                            disabled={busy}
                            className="w-full bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone disabled:opacity-60"
                        >
                            {busy ? "Inloggen…" : "Inloggen"}
                        </button>
                    </form>
                    <Link
                        to="/"
                        data-testid="admin-back-home"
                        className="mt-6 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:text-gold"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Terug naar site
                    </Link>
                </div>
            </div>
        );
    }

    const items = tab === "contact" ? messages : demos;
    const tabs = [
        { id: "contact", label: "Contactberichten", count: messages.length },
        { id: "demos", label: "Demo-aanvragen", count: demos.length },
    ];

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-bone">
            <header className="sticky top-0 z-10 border-b border-white/10 bg-black/60 backdrop-blur-xl">
                <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
                    <div className="flex items-baseline gap-4">
                        <p className="font-display text-xl tracking-[0.12em]">
                            CLARKE<span className="text-gold">.</span>
                        </p>
                        <span className="text-[11px] uppercase tracking-[0.3em] text-ash">
                            Aanvragen
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="admin-refresh-button"
                            onClick={load}
                            aria-label="Vernieuwen"
                            className="border border-white/15 p-3 text-ash transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>
                        <button
                            data-testid="admin-logout-button"
                            onClick={logout}
                            className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Uitloggen
                        </button>
                    </div>
                </div>
            </header>
            <div className="mx-auto max-w-[1200px] px-6 py-10">
                <div className="mb-8 flex gap-8 border-b border-white/10">
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`-mb-px border-b-2 pb-3 text-xs uppercase tracking-[0.25em] transition-colors ${
                                tab === t.id
                                    ? "border-gold text-gold"
                                    : "border-transparent text-ash hover:text-bone"
                            }`}
                        >
                            {t.label} ({t.count})
                        </button>
                    ))}
                </div>
                {items.length === 0 ? (
                    <p data-testid="admin-empty-state" className="py-16 text-center text-sm text-ash">
                        {tab === "contact"
                            ? "Nog geen contactberichten."
                            : "Nog geen demo-aanvragen."}
                    </p>
                ) : (
                    <div className="space-y-4">
                        {items.map((item) => (
                            <ItemCard
                                key={item.id}
                                kind={tab === "contact" ? "contact" : "demo"}
                                item={item}
                                onToggle={() => toggleHandled(tab === "contact" ? "contact" : "demo", item)}
                                onDelete={() => handleDelete(tab === "contact" ? "contact" : "demo", item)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
